import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { getOpportunities } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Package, MapPin, Calendar, Loader2, Truck, TrendingUp } from 'lucide-react';

const OpportunitiesPage = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      const data = await getOpportunities();
      setOpportunities(data);
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getVehicleTypeLabel = (type) => {
    const labels = {
      truck: 'Camion',
      van: 'Fourgon',
      refrigerated: 'Frigorifique',
      flatbed: 'Plateau',
      tanker: 'Citerne'
    };
    return labels[type] || type;
  };

  const filteredOpportunities = opportunities.filter(opp => {
    const matchesSearch = 
      opp.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opp.pickup_address.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opp.delivery_address.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesVehicle = vehicleFilter === 'all' || opp.required_vehicle_type === vehicleFilter;
    return matchesSearch && matchesVehicle;
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="opportunities-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight">
            Opportunités
          </h1>
          <p className="text-slate-500 mt-1">
            Découvrez les missions disponibles et faites vos offres
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-orange-50 border-orange-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Package className="w-8 h-8 text-orange-500" />
                <div>
                  <div className="text-2xl font-bold font-['JetBrains_Mono'] text-slate-900">{opportunities.length}</div>
                  <div className="text-sm text-slate-600">Missions dispo</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par titre ou adresse..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-opportunities-input"
                />
              </div>
              <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
                <SelectTrigger className="w-full md:w-48" data-testid="filter-vehicle-select">
                  <SelectValue placeholder="Type de véhicule" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les véhicules</SelectItem>
                  <SelectItem value="truck">Camion</SelectItem>
                  <SelectItem value="van">Fourgon</SelectItem>
                  <SelectItem value="refrigerated">Frigorifique</SelectItem>
                  <SelectItem value="flatbed">Plateau</SelectItem>
                  <SelectItem value="tanker">Citerne</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Opportunities List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : filteredOpportunities.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-4">
            {filteredOpportunities.map((opp) => (
              <Link 
                key={opp.id} 
                to={`/missions/${opp.id}`}
                data-testid={`opportunity-card-${opp.id}`}
              >
                <Card className="h-full hover:shadow-lg hover:border-orange-200 transition-all cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <h3 className="font-semibold text-lg text-slate-900 line-clamp-1">{opp.title}</h3>
                      <Badge className="bg-orange-100 text-orange-700 border-orange-200 shrink-0">
                        {getVehicleTypeLabel(opp.required_vehicle_type)}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <MapPin className="w-4 h-4 text-emerald-500 shrink-0" />
                        <span className="truncate">{opp.pickup_address}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <MapPin className="w-4 h-4 text-red-500 shrink-0" />
                        <span className="truncate">{opp.delivery_address}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                      <span>
                        <span className="font-['JetBrains_Mono'] text-slate-700">{opp.weight_kg}</span> kg
                      </span>
                      <span>
                        <span className="font-['JetBrains_Mono'] text-slate-700">{opp.volume_m3}</span> m³
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(opp.deadline).toLocaleDateString('fr-FR')}
                      </span>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t">
                      <div>
                        <div className="text-xs text-slate-500 uppercase tracking-wide">Budget</div>
                        <div className="font-['JetBrains_Mono'] font-bold text-lg text-slate-900">
                          {opp.budget_min}€ - {opp.budget_max}€
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-500">{opp.bids_count} offre(s)</div>
                        <Button size="sm" className="mt-1 bg-orange-500 hover:bg-orange-600">
                          Voir détails
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-1">Aucune opportunité trouvée</h3>
                <p className="text-slate-500">
                  {searchQuery || vehicleFilter !== 'all' 
                    ? 'Essayez avec d\'autres critères de recherche' 
                    : 'Revenez plus tard pour voir les nouvelles missions'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default OpportunitiesPage;
